import Papa from "papaparse";
import { getSeverityFromDepth } from './floodThresholds'; // 🔧 导入新的阈值函数

const ALLOWED_BOROUGHS = ["Brooklyn", "Queens"];

export const loadSensorData = async () => {
  try {
    // 加载 deployments.csv
    const deploymentsResponse = await fetch("/data/open_data_flood_sensor_deployments.csv");
    const deploymentsText = await deploymentsResponse.text();
    const deploymentsResult = Papa.parse(deploymentsText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimiter: ",",
      transformHeader: (header) => header.trim(),
    });

    // 加载 events.csv
    const eventsResponse = await fetch("/data/open_data_flood_events.csv");
    const eventsText = await eventsResponse.text();
    const eventsResult = Papa.parse(eventsText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimiter: ",",
      transformHeader: (header) => header.trim(),
    });

    // 按 Sensor ID 组织 events
    const eventsBySensorId = {};
    for (const e of eventsResult.data) {
      const sensorId = e["Sensor ID"];
      if (!sensorId) continue;
      if (!eventsBySensorId[sensorId]) eventsBySensorId[sensorId] = [];
      eventsBySensorId[sensorId].push({
        timestamp: e["Flood Start Datetime (GMT)"],
        water_depth_inches: e["Maximum Flood Depth (inches)"],
        severity: getSeverityFromDepth(e["Maximum Flood Depth (inches)"]), // 🔧 使用新的阈值函数
        description: `Flood event from ${e["Flood Start Datetime (GMT)"]} to ${e["Flood End Datetime (GMT)"]}`,
        duration: e["Total Duration (minutes)"],
      });
    }

    const sensors = deploymentsResult.data
      .filter((row) => {
        if (!row.Latitude || !row.Longitude) return false;
        if (!row["Sensor ID"]) return false;
        const borough = row["Sensor Location: Borough"];
        return ALLOWED_BOROUGHS.includes(borough);
      })
      .map((row) => {
        const sensor = transformSensorData(row);
        sensor.events = eventsBySensorId[sensor.id] || [];  // 🔔 用真实 events 填充
        return sensor;
      });

    return sensors;
  } catch (error) {
    console.error("❌ Error loading sensor data:", error);
    throw error;
  }
};

const transformSensorData = (csvRow) => {
  const latitude = parseFloat(csvRow.Latitude);
  const longitude = parseFloat(csvRow.Longitude);
  if (isNaN(latitude) || isNaN(longitude)) return null;

  return {
    id: csvRow["Sensor ID"],
    location: csvRow["Sensor Name"] || "Unknown Location",
    street: csvRow["Sensor Location: Street"] || "",
    neighborhood: csvRow["Sensor Location: Neighborhood"] || "",
    borough: csvRow["Sensor Location: Borough"] || "Unknown",
    coordinates: [latitude, longitude],
    installedDate: csvRow["Date Sensor Installed"],
    removedDate: csvRow["Date Sensor Removed (if applicable)"],
    deploymentDuration: csvRow["Sensor Deployed Duration (days)"],
    tidallyInfluenced: csvRow["Tidally Influenced"] === "t",
    heightAboveLowest: csvRow["Height Above Lowest Point (inches)"],
    relatedWeatherStation: csvRow["Related NYC Micronet weather service station"],
    relatedTideGauge: csvRow["Related Tide gauge"],
    isActive: !csvRow["Date Sensor Removed (if applicable)"],
    events: []  // 这里先为空，后面 merge
  };
};

// 🔧 删除了旧的getSeverityFromDepth函数，现在从floodThresholds.js导入

export const filterSensors = (sensors, filters) => {
  return sensors.filter((sensor) => {
    if (
      filters.borough !== "all" &&
      sensor.borough.toLowerCase() !== filters.borough.toLowerCase()
    ) return false;

    if (filters.severity !== "all" && sensor.events.length > 0) {
      const latestEvent = sensor.events[0];
      if (latestEvent.severity !== filters.severity) return false;
    }

    if (filters.dateRange !== "all" && sensor.events.length > 0) {
      const latestEvent = sensor.events[0];
      const eventDate = new Date(latestEvent.timestamp);
      const now = new Date();
      const daysDiff = (now - eventDate) / (1000 * 3600 * 24);
      switch (filters.dateRange) {
        case "day": if (daysDiff > 1) return false; break;
        case "week": if (daysDiff > 7) return false; break;
        case "month": if (daysDiff > 30) return false; break;
      }
    }

    if (filters.activeOnly && !sensor.isActive) return false;

    return true;
  });
};

// 🔧 更新统计函数以使用新的NWS命名
export const getSensorStats = (sensors) => {
  const stats = {
    total: sensors.length,
    active: sensors.filter((s) => s.isActive).length,
    byBorough: {},
    bySeverity: { minor: 0, moderate: 0, severe: 0 }, // 🔧 使用新的NWS命名
    tidalInfluenced: sensors.filter((s) => s.tidallyInfluenced).length,
  };
  
  sensors.forEach((sensor) => {
    const borough = sensor.borough;
    stats.byBorough[borough] = (stats.byBorough[borough] || 0) + 1;
    
    if (sensor.events.length > 0) {
      const severity = sensor.events[0].severity?.toLowerCase();
      // 🔧 标准化严重程度命名
      const severityMapping = {
        'mild': 'minor',     // 旧命名 -> 新命名
        'minor': 'minor',    // NWS标准
        'moderate': 'moderate', // 保持不变
        'severe': 'severe',  // 保持不变
        'major': 'severe'    // NWS major -> severe (代码中使用)
      };
      
      const mappedSeverity = severityMapping[severity] || 'minor';
      if (stats.bySeverity.hasOwnProperty(mappedSeverity)) {
        stats.bySeverity[mappedSeverity]++;
      }
    }
  });
  
  return stats;
};

export { ALLOWED_BOROUGHS };
export default {
  loadSensorData,
  filterSensors,
  getSensorStats,
  ALLOWED_BOROUGHS,
};